/**
 * Page route constants for the ConFusion-Web application.
 * This file centralizes all hardcoded route strings to avoid duplication
 * and provide a single source of truth for navigation.
 */

// Page Routes (pageRoute property)
export const PAGE_ROUTES = {
    // Personal Details Pages
    ID_NUMBER: 'id-number',
    CONTACT_DETAILS: 'contact-details',
    CONFIRM_DETAILS: 'confirm-details',
    CONFIRM_PERSONAL_DETAILS: 'confirm-personal-details',
    GENDER: 'gender',
    NAMES: 'names',

    // Car Details Pages
    CAR_DETAILS: 'car-details',
    HAS_TRACKING_DEVICE: 'has-tracking-device',
    CAR_MAKE: 'car-make',
    CAR_MODEL: 'car-model',
    CAR_YEAR: 'car-year',
    CAR_COLOUR: 'car-colour',
    CAR_TYPE: 'car-type',

    // Regular Driver Pages
    IS_REGULAR_DRIVER: 'is-regular-driver',

    // Add more page routes as discovered
} as const;

// Section Routes (sectionRoute property)
export const SECTION_ROUTES = {
    PERSONAL_DETAILS: 'personal-details',
    CAR_DETAILS: 'car-details',
    REGULAR_DRIVER: 'regular-driver',
    HOME_CONTENTS: 'home-contents',

    // Add more section routes as discovered
} as const;

// Base Routes (baseRoute property)
export const BASE_ROUTES = {
    CAR_INSURANCE: 'car-insurance',
    POLICY: 'policy',
    HOME_CONTENTS: 'home-contents',

    // Add more base routes as discovered
} as const;

// Type definitions for better type safety
export type PageRoute = (typeof PAGE_ROUTES)[keyof typeof PAGE_ROUTES];
export type SectionRoute = (typeof SECTION_ROUTES)[keyof typeof SECTION_ROUTES];
export type BaseRoute = (typeof BASE_ROUTES)[keyof typeof BASE_ROUTES];

// Helper function to create a complete route object
export const createRoute = (
    baseRoute: BaseRoute,
    sectionRoute: SectionRoute,
    pageRoute: PageRoute,
    goToStart: boolean = false,
) => ({
    baseRoute,
    sectionRoute,
    pageRoute,
    goToStart,
});

// Common route combinations for frequently used routes
export const COMMON_ROUTES = {
    ID_NUMBER_PAGE: createRoute(
        BASE_ROUTES.POLICY,
        SECTION_ROUTES.PERSONAL_DETAILS,
        PAGE_ROUTES.ID_NUMBER,
    ),
    CONTACT_DETAILS_PAGE: createRoute(
        BASE_ROUTES.POLICY,
        SECTION_ROUTES.PERSONAL_DETAILS,
        PAGE_ROUTES.CONTACT_DETAILS,
    ),
    CONFIRM_DETAILS_PAGE: createRoute(
        BASE_ROUTES.POLICY,
        SECTION_ROUTES.PERSONAL_DETAILS,
        PAGE_ROUTES.CONFIRM_DETAILS,
    ),
    CAR_DETAILS_PAGE: createRoute(
        BASE_ROUTES.CAR_INSURANCE,
        SECTION_ROUTES.CAR_DETAILS,
        PAGE_ROUTES.CAR_DETAILS,
    ),
    IS_REGULAR_DRIVER_PAGE: createRoute(
        BASE_ROUTES.CAR_INSURANCE,
        SECTION_ROUTES.REGULAR_DRIVER,
        PAGE_ROUTES.IS_REGULAR_DRIVER,
    ),
    GENDER_PAGE: createRoute(
        BASE_ROUTES.POLICY,
        SECTION_ROUTES.PERSONAL_DETAILS,
        PAGE_ROUTES.GENDER,
    ),
    HAS_TRACKING_DEVICE_PAGE: createRoute(
        BASE_ROUTES.CAR_INSURANCE,
        SECTION_ROUTES.CAR_DETAILS,
        PAGE_ROUTES.HAS_TRACKING_DEVICE,
    ),
} as const;
